import { useGetDocumentPageQuery } from "@api/material";
import LoadingSpinner from "@components/LoadingSpinner";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useToastEffect } from "@hooks/hooks";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";

// init pdfjs worker from CDN
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function SourceDocumentViewer({
  courseId,
  documentId,
  load = true,
}: {
  courseId: string;
  documentId: string;
  load?: boolean;
}) {
  const {
    data: presignedLinkDTO,
    isLoading,
    isError,
    error,
  } = useGetDocumentPageQuery({ courseId, documentId }, { skip: !load });
  useToastEffect({ isError, error });

  const [numPages, setNumPages] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation("chatPage");

  useEffect(() => {
    const CONTAINER_PADDING = 10;
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setPageWidth(container.offsetWidth - CONTAINER_PADDING);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const errorDiv = (
    <div className="m-20 flex flex-col items-center rounded-xl bg-neutral p-12">
      <div className="flex items-center gap-4">
        <ExclamationTriangleIcon className="h-8 w-8" />
        <div className="w-60">{t("sourceCarousel.errorLoadingPage")}</div>
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-4" ref={containerRef}>
      {isLoading ? (
        <LoadingSpinner viewHeight="min-h-60" />
      ) : presignedLinkDTO?.url ? (
        <Document
          file={presignedLinkDTO?.url}
          loading={<LoadingSpinner viewHeight="min-h-60" />}
          onLoadSuccess={onDocumentLoadSuccess}
          error={errorDiv}
        >
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth ?? 600}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      ) : (
        errorDiv
      )}
    </div>
  );
}
